import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import type { Settings } from '../../lib/settings-types.js';
import type {
  IngestedCostSemantics,
  InvoiceCheck,
  InvoiceInput,
  InvoiceProvider,
  OverheadInput,
  OverheadRow,
  PLReport,
} from '../../lib/types.js';
import { fmtMoney } from '../../lib/format.js';
import { VERDICT_LABEL, listOf, verdictTone } from '../../lib/pl-view.js';
import {
  hiddenColumnsNote,
  visibleColumns,
  type HideableColumn,
} from '../../lib/visibleColumns.js';
import {
  Button,
  Card,
  EmptyState,
  Field,
  SecretField,
  Select,
  StatusPill,
  TextInput,
  Toolbar,
  useConfirm,
} from '../../components/ui/index.js';
import { ADMIN_NOTE, errorMessage, fmtWhen, useIsAdmin, useSettingsPost } from './common.js';
import { ProxyRatesCard } from './ProxyRatesCard.js';

/**
 * Buy side: the Airman connection (daily catalog + cost pull) and where its
 * suggestions land; the Undetect bill (the launcher's control total); and the
 * hosting bill (Civo's usage, priced at the list rates - an overhead); then
 * the three cards that close the loop: what the ingested cost columns mean
 * (P7), the invoices checked against the mirror (P9) and the other fixed
 * costs charged pro-rata (P10). Nothing counts toward a feed's profit until a
 * human confirms a link on the Matching page, and no overhead ever counts
 * toward a feed at all.
 */

/** The codes the last Civo sync could not price, read from the stored
 * summary. Anything that is not a string array reads as "none": the card must
 * never fail to render because a summary is old or malformed. */
function unpricedCodesOf(summary: Record<string, unknown> | null): string[] {
  const v = summary?.unpricedCodes;
  return Array.isArray(v) ? v.filter((c): c is string => typeof c === 'string') : [];
}

type RateBasis = 'hour' | 'gb-hour';

/** The basis the last sync reported per unpriced code ('gb-hour' when any of
 * its lines carried a size, else 'hour'); anything malformed reads as none. */
function unpricedBasisOf(summary: Record<string, unknown> | null): Record<string, RateBasis> {
  const v = summary?.unpricedBasis;
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return {};
  const out: Record<string, RateBasis> = {};
  for (const [code, basis] of Object.entries(v as Record<string, unknown>)) {
    if (basis === 'hour' || basis === 'gb-hour') out[code] = basis;
  }
  return out;
}

/** A count out of the summary; anything that is not a finite number >= 0 reads as 0. */
function countOf(summary: Record<string, unknown> | null, key: string): number {
  const v = summary?.[key];
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : 0;
}

/**
 * How an override for this code is applied (server costs/civo-prices.ts): the
 * table's GB-hour codes, and any code whose lines carried a size, are priced
 * per GB-hour; everything else per hour. The label has to say which - the
 * documented remedy for an unpriced object store is "enter 0.00001488 per
 * GB-hour", and typed into a per-hour field that under-prices it 500-fold.
 */
function rateUnit(code: string, basis: Record<string, RateBasis>): 'hour' | 'GB-hour' {
  return code === 'volume' || code === 'objectstore' || basis[code] === 'gb-hour'
    ? 'GB-hour'
    : 'hour';
}

/**
 * A number the operator actually typed: finite, >= 0, <= max. Blank and
 * whitespace are NOT a number - Number('') is 0, and a field emptied to
 * retype must never save as 0 (a 20% VAT silently becoming 0 drops the bill
 * by a sixth with a green note).
 */
function typedNumber(v: string, max = Number.POSITIVE_INFINITY): number | null {
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 && n <= max ? n : null;
}

/** A rate field is valid empty (remove the override - the list price applies) or as a typed number >= 0. */
function isRateInput(v: string): boolean {
  return v.trim() === '' || typedNumber(v) != null;
}

/**
 * An invoice rate the operator typed: a finite number > 0. Blank is not a
 * number (Number('') is 0 - the pattern that bit the Undetect card, R3: a
 * field emptied to retype saved as $0/GB and the nightly sync re-priced 30
 * days of bandwidth as free, green note and all), and 0 is refused here for
 * the reason the server refuses it: a $0 launch or GB rate is never an
 * invoice.
 */
function typedRate(v: string): number | null {
  const n = typedNumber(v);
  return n != null && n > 0 ? n : null;
}

/** A string list out of a summary (the days a sync skipped); anything else reads as none. */
/** The days the last run's history guard refused, with their reasons. */
function refusedDaysOf(summary: Record<string, unknown> | null): { day: string; reason: string }[] {
  const v = summary?.['refusedDays'];
  if (!Array.isArray(v)) return [];
  return v
    .filter(
      (r): r is { day: string; reason?: unknown } =>
        !!r && typeof r === 'object' && typeof (r as { day?: unknown }).day === 'string',
    )
    .map((r) => ({ day: r.day, reason: typeof r.reason === 'string' ? r.reason : '' }));
}

function stringListOf(summary: Record<string, unknown> | null, key: string): string[] {
  const v = summary?.[key];
  return Array.isArray(v) ? v.filter((c): c is string => typeof c === 'string') : [];
}

/** POST /api/undetect-sync on success (server matching/undetect.ts UndetectSummary). */
interface UndetectSyncResponse {
  ok: boolean;
  days?: number;
  from?: string | null;
  to?: string | null;
  launches?: number;
  gb?: number;
  totalUsd?: number;
  /** rows whose counts were unreadable - skipped and counted, never coerced to 0. */
  unreadableRows?: number;
  /** breakdown rows dated outside the asked month - dropped and counted (N1); the API is out of contract. */
  rowsOutsideMonth?: number;
  /** series days the month breakdown had no bandwidth for - skipped, never written as 0 bytes. */
  bandwidthMissingDays?: string[];
  /** days the history guard refused (would zero or shrink a billed day); the others were mirrored */
  refusedDays?: { day: string; reason: string }[];
}

/** The secrets a changed TRAFFIC_SECRET_KEY orphans, by their meta key, as the operator knows them. */
const SECRET_NAMES: Record<string, string> = {
  anthropic_api_key: 'Anthropic API key (APIs)',
  undetect_key: 'Undetect dashboard key (below)',
  civo_key: 'Civo API key (below)',
  airman_token: 'Airman token (below)',
  alert_webhook_url: 'alert webhook URL (APIs)',
};

/** POST /api/civo-sync on success (server costs/civo.ts CivoSummary). */
interface CivoSyncResponse {
  ok: boolean;
  days?: number;
  from?: string | null;
  to?: string | null;
  hours?: number;
  costUsd?: number;
  unpricedHours?: number;
  unpricedCodes?: string[];
  /** lines dropped for an unreadable num_hours - their hours are missing from the bill. */
  skippedLines?: number;
  unpricedBasis?: Record<string, RateBasis>;
}

export function BuyPane({ settings }: { settings: Settings }) {
  const qc = useQueryClient();
  const isAdmin = useIsAdmin();
  const [airBase, setAirBase] = useState(settings.airman.baseUrl ?? '');
  const [note, setNote] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const setAirman = useSettingsPost<{ baseUrl: string; token?: string }>('/api/settings/airman');
  const saveUrlOnly = useMutation({
    mutationFn: () => setAirman.mutateAsync({ baseUrl: airBase }),
  });

  // Undetect (the launcher bill). Same shape as the Civo card below: a
  // failing sync changes the pill and is named on the card; only a rate the
  // admin typed is ever sent; Disconnect says "removed" once the server
  // agrees. The basis flips the launch model on and off, so the per-feed
  // profit table is refetched with the P&L.
  const und = settings.undetect;
  const [undNote, setUndNote] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const undUnreadableRows = countOf(und.lastSummary, 'unreadableRows');
  const undRowsOutsideMonth = countOf(und.lastSummary, 'rowsOutsideMonth');
  const undMissingDays = stringListOf(und.lastSummary, 'bandwidthMissingDays');
  // What the breakdown rows carry, for when no vendor field was recognised (every day folded into one unnamed vendor).
  const undBreakdownFields = stringListOf(und.lastSummary, 'breakdownFields');
  const undVendorsSeen = stringListOf(und.lastSummary, 'vendorsSeen');
  const undRefusedDays = refusedDaysOf(und.lastSummary);
  const undPill: { tone: 'ok' | 'warn' | 'neutral'; text: string } = und.active
    ? und.lastError
      ? { tone: 'warn', text: 'Sync failing' }
      : { tone: 'ok', text: 'Control total' }
    : und.configured
      ? {
          tone: 'warn',
          text: und.lastError ? 'Key saved — sync failed' : 'Key saved — sync to activate',
        }
      : { tone: 'neutral', text: 'Not connected' };
  const setUndetect = useSettingsPost<{ key?: string | null }>('/api/settings/undetect');
  const invalidateCostViews = () => {
    void qc.invalidateQueries({ queryKey: ['pl'] });
    void qc.invalidateQueries({ queryKey: ['profit'] });
  };
  const undetectSync = useMutation({
    mutationFn: () => api.post<UndetectSyncResponse>('/api/undetect-sync'),
    onSuccess: (res) => {
      const unreadable = res.unreadableRows ?? 0;
      const outside = res.rowsOutsideMonth ?? 0;
      const missing = res.bandwidthMissingDays ?? [];
      const refused = res.refusedDays ?? [];
      setUndNote({
        // Skipped rows and days are missing from the bill, so the total
        // shown is low: a warning, not a success. Rows outside the asked
        // month mean the API ignored `month` (N1): the same warning (F7). A
        // refused day kept its old figure - named, so a credit can be dealt
        // with by hand.
        tone: unreadable > 0 || outside > 0 || missing.length || refused.length ? 'error' : 'ok',
        text:
          `Synced ${res.days ?? 0} days (${res.from ?? '?'} → ${res.to ?? '?'}): ${Number(res.launches ?? 0).toLocaleString()} launches, ${res.gb ?? 0} GB, $${Number(res.totalUsd ?? 0).toFixed(2)}.` +
          (unreadable > 0
            ? ` ${unreadable} row${unreadable === 1 ? '' : 's'} unreadable — skipped, missing from the bill.`
            : '') +
          (outside > 0
            ? ` ${outside} breakdown row${outside === 1 ? '' : 's'} dated outside the asked month — dropped; the API ignored the month it was asked for.`
            : '') +
          (missing.length
            ? ` No bandwidth for ${missing.join(', ')} — those days were left as they were.`
            : '') +
          (refused.length
            ? ` Refused ${refused.map((r) => r.day).join(', ')} — the answer would shrink or zero a billed day; those days kept their figures (a credit needs the row deleted by hand).`
            : ''),
      });
      void qc.invalidateQueries({ queryKey: ['settings'] });
      invalidateCostViews();
    },
    onError: (e) => setUndNote({ tone: 'error', text: errorMessage(e) }),
  });
  const disconnectUndetect = async () => {
    // Say "removed" only once the server agrees: a failed POST (session
    // lapsed, 5xx) leaves the key stored, and the refetched pill would
    // contradict a note that said it was gone.
    try {
      await setUndetect.mutateAsync({ key: null });
      setUndNote({
        tone: 'ok',
        text: 'Key removed — the P&L is back on the Airman basis; the mirrored days are kept.',
      });
      invalidateCostViews();
    } catch (e) {
      setUndNote({
        tone: 'error',
        text: `Disconnect failed — the key is still stored. ${errorMessage(e)}`,
      });
    }
  };

  // Hosting (Civo). The key is stored on save and the first sync runs at
  // once; a failed sync leaves the key in place (the pill says so) and Sync
  // now retries. Rates: only what the user has typed lives in rateDrafts - a
  // code they have not touched shows its stored override, so a settings
  // refetch (a sync surfacing a new unpriced code) never drops their input.
  const civo = settings.civo;
  const [civoNote, setCivoNote] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [vatPct, setVatPct] = useState(String(civo.vatPct));
  const [rateDrafts, setRateDrafts] = useState<Record<string, string>>({});
  const unpricedCodes = unpricedCodesOf(civo.lastSummary);
  const unpricedBasis = unpricedBasisOf(civo.lastSummary);
  const skippedLines = countOf(civo.lastSummary, 'skippedLines');
  const rateCodes = [...new Set([...unpricedCodes, ...Object.keys(civo.rates)])].sort();
  // The pill is the one thing read at a glance, so a failing sync must
  // change it: with the line still on the P&L the mirror is going stale (each
  // day the window loses one mirrored day); without, the key is waiting.
  const civoPill: { tone: 'ok' | 'warn' | 'neutral'; text: string } = civo.active
    ? civo.lastError
      ? { tone: 'warn', text: 'Sync failing' }
      : { tone: 'ok', text: 'On the P&L' }
    : civo.configured
      ? {
          tone: 'warn',
          text: civo.lastError ? 'Key saved — sync failed' : 'Key saved — sync to activate',
        }
      : { tone: 'neutral', text: 'Not connected' };
  const rateValue = (code: string): string => {
    const draft = rateDrafts[code];
    if (draft != null) return draft;
    const stored = civo.rates[code];
    return stored == null ? '' : String(stored);
  };
  const setCivo = useSettingsPost<{
    key?: string | null;
    vatPct?: number | null;
    rates?: Record<string, number | null>;
  }>('/api/settings/civo');
  const civoSync = useMutation({
    mutationFn: () => api.post<CivoSyncResponse>('/api/civo-sync'),
    onSuccess: (res) => {
      const unpriced = res.unpricedCodes ?? [];
      const skipped = res.skippedLines ?? 0;
      setCivoNote({
        // Unpriced hours are counted but not charged, and skipped lines are
        // not even counted, so the total shown is low: a warning, not a
        // success.
        tone: unpriced.length || skipped > 0 ? 'error' : 'ok',
        text:
          `Synced ${res.days ?? 0} days (${res.from ?? '?'} → ${res.to ?? '?'}): ${Number(res.hours ?? 0).toLocaleString()} h, $${Number(res.costUsd ?? 0).toFixed(2)} ex-VAT.` +
          (unpriced.length
            ? ` No rate for: ${unpriced.join(', ')} — ${Number(res.unpricedHours ?? 0).toLocaleString()} h counted but not priced.`
            : '') +
          (skipped > 0
            ? ` ${skipped} usage line${skipped === 1 ? '' : 's'} skipped (unreadable hours) — missing from the bill.`
            : ''),
      });
      void qc.invalidateQueries({ queryKey: ['settings'] });
      void qc.invalidateQueries({ queryKey: ['pl'] });
    },
    onError: (e) => setCivoNote({ tone: 'error', text: errorMessage(e) }),
  });
  // Only a number the admin typed is ever sent: a blank field disables Save
  // VAT rather than saving 0 (Number('') is 0 - the pattern that bit the
  // Undetect card's rate fields).
  const vatTyped = typedNumber(vatPct, 100);
  const saveVat = async () => {
    if (vatTyped == null) return;
    try {
      await setCivo.mutateAsync({ vatPct: vatTyped });
      setCivoNote({
        tone: 'ok',
        text:
          vatTyped > 0
            ? `VAT saved: ${vatTyped}% is added to the ex-VAT bill at read time.`
            : 'VAT saved: the P&L shows the ex-VAT bill.',
      });
      void qc.invalidateQueries({ queryKey: ['pl'] });
    } catch (e) {
      setCivoNote({ tone: 'error', text: errorMessage(e) });
    }
  };
  const saveRates = async () => {
    // Every listed code is sent: a typed number sets its override, an empty
    // field removes it so the list price applies again (a null for a code
    // with no override is a no-op). Never Number('') -> 0: a blank is a
    // removal, not a free rate.
    const rates: Record<string, number | null> = {};
    for (const code of rateCodes) {
      const raw = rateValue(code);
      if (raw.trim() === '') {
        rates[code] = null;
        continue;
      }
      const n = typedNumber(raw);
      if (n == null) {
        setCivoNote({
          tone: 'error',
          text: `${code}: enter a number ≥ 0, or leave it empty for the list price.`,
        });
        return;
      }
      rates[code] = n;
    }
    try {
      await setCivo.mutateAsync({ rates });
      setRateDrafts({});
      setCivoNote({ tone: 'ok', text: 'Rates saved — sync now to re-price the mirrored days.' });
    } catch (e) {
      setCivoNote({ tone: 'error', text: errorMessage(e) });
    }
  };
  const disconnectCivo = async () => {
    // Say "removed" only once the server agrees: a failed POST (session
    // lapsed, 5xx) leaves the root key stored, and the refetched pill would
    // contradict a note that said it was gone.
    try {
      await setCivo.mutateAsync({ key: null });
      setCivoNote({
        tone: 'ok',
        text: 'Key removed — the hosting line leaves the P&L; the mirrored days are kept.',
      });
      void qc.invalidateQueries({ queryKey: ['pl'] });
    } catch (e) {
      setCivoNote({
        tone: 'error',
        text: `Disconnect failed — the key is still stored. ${errorMessage(e)}`,
      });
    }
  };

  const airmanSync = useMutation({
    mutationFn: () => api.post<Record<string, number>>('/api/airman-sync'),
    onSuccess: (res) => {
      setNote({
        tone: 'ok',
        text: `Synced: ${res.destinations ?? 0} destinations, ${res.events ?? 0} changes, ${res.rows ?? 0} daily rows.`,
      });
      void qc.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (e) => setNote({ tone: 'error', text: errorMessage(e) }),
  });

  // Pull all history (2026-09-15): Airman one calendar month at a time until
  // two in a row are empty, then Undetect for as far back as its API answers.
  const backfill = useMutation({
    mutationFn: async () => {
      const a = await api.post<{
        months: number;
        rows: number;
        from: string | null;
        to: string | null;
        emptyMonths: string[];
        historyFrom: string | null;
      }>('/api/airman-backfill');
      let u: { days?: number; from?: string | null; to?: string | null; window?: string } | null =
        null;
      let undetectError = '';
      if (settings.undetect.configured) {
        try {
          u = await api.post('/api/undetect-backfill');
        } catch (e) {
          undetectError = errorMessage(e);
        }
      }
      return { a, u, undetectError };
    },
    onSuccess: ({ a, u, undetectError }) => {
      const airman = `Airman: ${a.rows} daily rows over ${a.months} month${a.months === 1 ? '' : 's'}${a.from ? ` (${a.from} → ${a.to})` : ''}; history now from ${a.historyFrom ?? '—'}.`;
      const undetect = !settings.undetect.configured
        ? ''
        : u
          ? ` Undetect: ${u.days ?? 0} days${u.from ? ` (${u.from} → ${u.to})` : ''}${u.window === '30d' ? ' — its API answers 30 days at most, so older launches cannot be mirrored' : ''}.`
          : ` Undetect: ${undetectError}`;
      setNote({ tone: undetectError ? 'error' : 'ok', text: airman + undetect });
      void qc.invalidateQueries({ queryKey: ['settings'] });
      void qc.invalidateQueries({ queryKey: ['pl'] });
    },
    onError: (e) => setNote({ tone: 'error', text: errorMessage(e) }),
  });

  const summary = settings.airman.lastSummary;
  const undecryptable = settings.undecryptable ?? [];

  return (
    <div>
      {/* R26: after a TRAFFIC_SECRET_KEY rotation the stored secrets cannot
          be read. The settings read names them instead of failing, so this
          page can still be used to re-enter each one; until then their
          syncs fail and the pills say so. */}
      {undecryptable.length ? (
        <div className="warnbox" role="alert">
          <b>TRAFFIC_SECRET_KEY changed</b> —{' '}
          {undecryptable.length === 1
            ? 'a stored secret'
            : `${undecryptable.length} stored secrets`}{' '}
          can no longer be decrypted and must be re-entered:{' '}
          {undecryptable.map((k) => SECRET_NAMES[k] ?? k).join('; ')}. Until then the syncs that use
          them fail (their pills say so). Nothing else is lost: mirrored days and links are not
          encrypted.
        </div>
      ) : null}
      <Card
        title="Airman"
        status={
          <StatusPill tone={settings.airman.configured ? 'ok' : 'neutral'}>
            {settings.airman.configured ? 'Connected' : 'Not connected'}
          </StatusPill>
        }
        description={
          <>
            Pulls the buy-side destinations catalog (which becomes link suggestions on the{' '}
            <a href="/matching">Matching page</a>) and the daily volume/cost rows. Runs daily; sync
            manually any time.
          </>
        }
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => airmanSync.mutate()}
              loading={airmanSync.isPending}
              disabled={!settings.airman.configured}
            >
              Sync now
            </Button>
            {isAdmin ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => backfill.mutate()}
                loading={backfill.isPending}
                disabled={!settings.airman.configured || airmanSync.isPending}
                title="Every daily row Airman still serves, one month at a time until two months in a row are empty; then Undetect for as far back as its API answers. Nothing is deleted. Takes a minute."
              >
                Pull all history
              </Button>
            ) : null}
          </>
        }
      >
        {/* State as facts you can read at a glance, rather than a run-on
            sentence in muted 12px with "Configured: yes" inside it. */}
        <dl className="cfg-facts">
          <div>
            <dt>Base URL</dt>
            <dd className="mono">{settings.airman.baseUrl ?? '—'}</dd>
          </div>
          <div>
            <dt>Last sync</dt>
            <dd>{fmtWhen(settings.airman.lastSync)}</dd>
          </div>
          <div>
            <dt>History from</dt>
            <dd>{settings.airman.historyFrom ?? '—'}</dd>
          </div>
          {summary ? (
            <div>
              <dt>Last run</dt>
              <dd>
                {Object.entries(summary)
                  .map(([k, v]) => `${k} ${v}`)
                  .join(', ')}
              </dd>
            </div>
          ) : null}
        </dl>

        {note ? (
          <p className={note.tone === 'error' ? 'ui-field__error' : 'ui-field__hint'} role="status">
            {note.text}
          </p>
        ) : null}

        {isAdmin ? (
          <>
            <SecretField
              label="Airman token"
              isSet={settings.airman.configured}
              placeholder="amn_… (read-only token)"
              hint="A read-only token. Stored encrypted; never shown again."
              saveLabel="Save connection"
              extra={
                <Field label="Base URL">
                  {(aria) => (
                    <TextInput
                      {...aria}
                      value={airBase}
                      placeholder="https://…"
                      onChange={(e) => setAirBase(e.target.value)}
                    />
                  )}
                </Field>
              }
              disabled={!airBase.trim()}
              onSave={(token) => setAirman.mutateAsync({ baseUrl: airBase, token })}
            />
            {settings.airman.configured ? (
              <Toolbar className="cfg-actions">
                <Button
                  variant="ghost"
                  loading={saveUrlOnly.isPending}
                  disabled={
                    !airBase.trim() ||
                    airBase.trim().replace(/\/+$/, '') === settings.airman.baseUrl
                  }
                  onClick={() => saveUrlOnly.mutate()}
                >
                  Save URL only (keep the stored token)
                </Button>
                {saveUrlOnly.isError ? (
                  <span className="ui-field__error">{errorMessage(saveUrlOnly.error)}</span>
                ) : null}
                {saveUrlOnly.isSuccess ? (
                  <span className="ui-saved ui-saved--ok" role="status">
                    ✓ URL saved
                  </span>
                ) : null}
              </Toolbar>
            ) : null}
          </>
        ) : (
          <EmptyState>{ADMIN_NOTE}</EmptyState>
        )}
      </Card>

      <Card
        title="Undetect (launcher bill)"
        status={<StatusPill tone={undPill.tone}>{undPill.text}</StatusPill>}
        description="The launcher's own daily totals — launches and proxy bandwidth, priced at the invoice rates. Once a day is mirrored this is what the operating P&L costs against: Airman's per-destination spend becomes a breakdown reconciled to it day by day, and the bill is the launch cost. Check each month's invoice against the mirrored total before trusting the launch count."
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => undetectSync.mutate()}
            loading={undetectSync.isPending}
            disabled={!und.configured}
          >
            Sync now
          </Button>
        }
      >
        <dl className="cfg-facts">
          <div>
            <dt>Rates</dt>
            <dd>
              {und.rate
                ? `$${und.rate.perLaunchUsd} per launch · $${und.rate.perGbUsd.toFixed(2)} per GB` +
                  (und.rate.baseMonthUsd ? ` · $${und.rate.baseMonthUsd.toFixed(2)} a month` : '') +
                  ` · ${und.rateEntries} entr${und.rateEntries === 1 ? 'y' : 'ies'} on the Proxy rates schedule below`
                : 'no rate in effect today — add one under Proxy rates below, or the sync refuses to price'}
            </dd>
          </div>
          <div>
            <dt>Last sync</dt>
            <dd>{fmtWhen(und.lastSync)}</dd>
          </div>
          <div>
            <dt>History from</dt>
            <dd>{und.historyFrom ?? '—'}</dd>
          </div>
          {und.lastSummary ? (
            <div>
              <dt>Last run</dt>
              <dd>
                {Object.entries(und.lastSummary)
                  // The skipped rows and days get their own lines below.
                  .filter(
                    ([k]) =>
                      k !== 'unreadableRows' &&
                      k !== 'bandwidthMissingDays' &&
                      k !== 'rowsOutsideMonth' &&
                      k !== 'refusedDays',
                  )
                  .map(([k, v]) => `${k} ${String(v)}`)
                  .join(', ')}
              </dd>
            </div>
          ) : null}
        </dl>
        {und.lastError ? (
          <p className="ui-field__error">
            Last sync failed: {und.lastError}
            {und.active
              ? ' — the P&L still costs against the days the last good sync mirrored, and the window loses one of them a day until a sync succeeds.'
              : ' — the key is saved; fix the cause and Sync now.'}
          </p>
        ) : null}
        {undUnreadableRows > 0 ? (
          <p className="ui-field__error">
            {undUnreadableRows} row{undUnreadableRows === 1 ? '' : 's'} unreadable on the last sync
            (a count that was not a number) — skipped, missing from the bill; see the worker log.
          </p>
        ) : null}
        {undRefusedDays.length ? (
          <p className="form-err" role="status">
            The last sync refused {undRefusedDays.map((r) => r.day).join(', ')} — the answer would
            shrink or zero a billed day, so those days kept their figures while the rest were
            mirrored. A genuine credit needs the day deleted by hand first:{' '}
            {undRefusedDays.map((r) => r.reason).join(' · ')}
          </p>
        ) : null}
        {undMissingDays.length ? (
          <p className="ui-field__error">
            No bandwidth in the month breakdown for {undMissingDays.join(', ')} — those days were
            left as they were, never written as 0 GB.
          </p>
        ) : null}
        {undRowsOutsideMonth > 0 ? (
          <p className="ui-field__error">
            {undRowsOutsideMonth} breakdown row{undRowsOutsideMonth === 1 ? '' : 's'} dated outside
            the asked month on the last sync — dropped, never merged; the API ignored the month it
            was asked for.
          </p>
        ) : null}
        {undNote ? (
          <p
            className={undNote.tone === 'error' ? 'ui-field__error' : 'ui-field__hint'}
            role="status"
          >
            {undNote.text}
          </p>
        ) : null}
        {isAdmin ? (
          <>
            <SecretField
              label="Dashboard key"
              isSet={und.configured}
              placeholder="the key from the dashboard URL (?key=…)"
              hint="Stored encrypted; never shown again. Only the stats endpoints are read."
              saveLabel="Save key and sync"
              onSave={async (key) => {
                await setUndetect.mutateAsync({ key });
                // The basis only switches once a day is mirrored: run the
                // first sync now so the key never sits half-applied.
                undetectSync.mutate();
              }}
            />
            {undVendorsSeen.length &&
            undVendorsSeen.every((v) => v === '') &&
            undBreakdownFields.length ? (
              <p className="ui-field__hint">
                Undetect’s bandwidth breakdown names no vendor the app recognises, so every day is
                priced at the default entry. Its rows carry: {undBreakdownFields.join(', ')}.
              </p>
            ) : null}
            <Toolbar className="cfg-actions">
              {und.configured ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={setUndetect.isPending}
                  onClick={() => void disconnectUndetect()}
                >
                  Disconnect
                </Button>
              ) : null}
            </Toolbar>
          </>
        ) : (
          <EmptyState>{ADMIN_NOTE}</EmptyState>
        )}
      </Card>

      <ProxyRatesCard isAdmin={isAdmin} />

      <Card
        title="Hosting (Civo)"
        status={<StatusPill tone={civoPill.tone}>{civoPill.text}</StatusPill>}
        description="The infrastructure bill, mirrored daily from Civo's usage report and priced at the list rates. An overhead: charged in the operating P&L and carried whole in Unattributed, never spread across feeds. Stored ex-VAT — set a VAT % only if the business does not reclaim it. Compare each month's mirrored total with the invoice subtotal; a code with no rate is counted in hours but not priced until one is entered here."
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => civoSync.mutate()}
            loading={civoSync.isPending}
            disabled={!civo.configured}
          >
            Sync now
          </Button>
        }
      >
        <dl className="cfg-facts">
          <div>
            <dt>Last sync</dt>
            <dd>{fmtWhen(civo.lastSync)}</dd>
          </div>
          <div>
            <dt>VAT applied</dt>
            <dd>
              {civo.vatPct > 0 ? `${civo.vatPct}% on top of the ex-VAT bill` : 'none — ex-VAT'}
            </dd>
          </div>
          {civo.lastSummary ? (
            <div>
              <dt>Last run</dt>
              <dd>
                {Object.entries(civo.lastSummary)
                  // The codes and their basis get their own lines below.
                  .filter(([k]) => k !== 'unpricedCodes' && k !== 'unpricedBasis')
                  .map(([k, v]) => `${k} ${String(v)}`)
                  .join(', ')}
              </dd>
            </div>
          ) : null}
        </dl>
        {civo.lastError ? (
          <p className="ui-field__error">
            Last sync failed: {civo.lastError}
            {civo.active
              ? ' — the P&L still charges the days the last good sync mirrored, and the window loses one of them a day until a sync succeeds.'
              : ' — the key is saved; fix the cause and Sync now.'}
          </p>
        ) : null}
        {unpricedCodes.length ? (
          <p className="ui-field__error">
            No rate for: {unpricedCodes.join(', ')} — those hours are counted but not priced until a
            rate is entered below.
          </p>
        ) : null}
        {skippedLines > 0 ? (
          <p className="ui-field__error">
            {skippedLines} usage line{skippedLines === 1 ? '' : 's'} skipped on the last sync (no
            readable hours) — missing from the bill entirely; see the worker log.
          </p>
        ) : null}
        {civoNote ? (
          <p
            className={civoNote.tone === 'error' ? 'ui-field__error' : 'ui-field__hint'}
            role="status"
          >
            {civoNote.text}
          </p>
        ) : null}
        {isAdmin ? (
          <>
            <SecretField
              label="Civo API key"
              isSet={civo.configured}
              placeholder="the account's API key (Profile → Security)"
              hint="Civo's one per-account key — it can create and destroy servers. Stored encrypted; only ever sent to api.civo.com; never shown again."
              saveLabel="Save key and sync"
              onSave={async (key) => {
                await setCivo.mutateAsync({ key });
                // The line only reaches the P&L once a day is mirrored: run
                // the first sync now so the key never sits half-applied. If
                // it fails the key stays saved and Sync now retries.
                civoSync.mutate();
              }}
            />
            <Field label="VAT % to add (0 = ex-VAT, reclaimed)">
              {(aria) => (
                <TextInput
                  {...aria}
                  type="number"
                  min={0}
                  max={100}
                  step="0.1"
                  inputMode="decimal"
                  value={vatPct}
                  onChange={(e) => setVatPct(e.target.value)}
                />
              )}
            </Field>
            <Toolbar className="cfg-actions">
              <Button
                size="sm"
                loading={setCivo.isPending}
                disabled={vatTyped == null}
                onClick={() => void saveVat()}
              >
                Save VAT
              </Button>
            </Toolbar>
            {rateCodes.length ? (
              rateCodes.map((code) => (
                <Field key={code} label={`${code} — USD per ${rateUnit(code, unpricedBasis)}`}>
                  {(aria) => (
                    <TextInput
                      {...aria}
                      type="number"
                      min={0}
                      // Not a fixed step: the object-store rate is 0.00001488
                      // per GB-hour, which a 1e-6 step would flag as invalid.
                      step="any"
                      inputMode="decimal"
                      value={rateValue(code)}
                      placeholder="no rate — enter one, or leave empty for the list price"
                      onChange={(e) => setRateDrafts((d) => ({ ...d, [code]: e.target.value }))}
                    />
                  )}
                </Field>
              ))
            ) : (
              <p className="ui-field__hint">
                Rates come from the built-in list-price table (2026-09). A code Civo reports that
                the table does not know appears here after a sync, with a field for its rate.
              </p>
            )}
            <Toolbar className="cfg-actions">
              {rateCodes.length ? (
                <Button
                  size="sm"
                  loading={setCivo.isPending}
                  disabled={!rateCodes.every((code) => isRateInput(rateValue(code)))}
                  onClick={() => void saveRates()}
                >
                  Save rates
                </Button>
              ) : null}
              {civo.configured ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={setCivo.isPending}
                  onClick={() => void disconnectCivo()}
                >
                  Disconnect
                </Button>
              ) : null}
            </Toolbar>
          </>
        ) : (
          <EmptyState>{ADMIN_NOTE}</EmptyState>
        )}
      </Card>

      <IngestedCostCard settings={settings} isAdmin={isAdmin} />
      <InvoicesCard isAdmin={isAdmin} />
      <OverheadsCard isAdmin={isAdmin} />

      <Card title="How cost reaches profit">
        <p className="table-note">
          Cost only ever counts against a feed after its link is <b>confirmed</b> by a person —
          provisional suggestions never move the profit numbers. The hosting bill is an overhead and
          never reaches a feed.
        </p>
        <Toolbar className="cfg-actions">
          <Button variant="ghost" size="sm" onClick={() => (window.location.href = '/matching')}>
            Go to Matching
          </Button>
        </Toolbar>
      </Card>
    </div>
  );
}

/* ------------------------------------------------------ ingested costs (P7) */

/**
 * The three ingested per-row cost columns (traffic_cost / compute_cost /
 * llm_cost) came from the legacy import with no record of what the Python
 * app meant by them. Until the owner says, they are "unconfirmed": summed
 * into the Cost tile as today, absent from the P&L, and the P&L card carries
 * a banner. 'real' puts them on every P&L layer and per feed; 'model' drops
 * them from the Summary too. Both branches exist so the answer is a setting,
 * never a guess (R11).
 */
function IngestedCostCard({ settings, isAdmin }: { settings: Settings; isAdmin: boolean }) {
  const qc = useQueryClient();
  const state = settings.ingestedCost;
  const [choice, setChoice] = useState<IngestedCostSemantics>(state?.semantics ?? 'unconfirmed');
  const [note, setNote] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const save = useSettingsPost<{ semantics: 'real' | 'model' | null }>(
    '/api/settings/ingested-cost',
  );
  if (!state) return null;
  const rows = state.rows;
  const pill: { tone: 'ok' | 'warn' | 'neutral'; text: string } =
    state.semantics === 'unconfirmed'
      ? rows > 0
        ? { tone: 'warn', text: 'Unconfirmed' }
        : { tone: 'neutral', text: 'No rows' }
      : state.semantics === 'real'
        ? { tone: 'ok', text: 'Real costs — on the P&L' }
        : { tone: 'neutral', text: 'Modelled — ignored' };
  const submit = async () => {
    try {
      await save.mutateAsync({ semantics: choice === 'unconfirmed' ? null : choice });
      setNote({
        tone: 'ok',
        text:
          choice === 'real'
            ? 'Saved: the ingested costs are real — a line in every P&L layer and per feed, and still in the Cost tile.'
            : choice === 'model'
              ? 'Saved: the ingested costs were the old model — dropped from the Cost tile and never on the P&L.'
              : 'Back to unconfirmed: counted in the Cost tile as before, absent from the P&L, banner shown.',
      });
      for (const key of ['pl', 'profit', 'summary', 'taxonomy'])
        void qc.invalidateQueries({ queryKey: [key] });
    } catch (e) {
      setNote({ tone: 'error', text: errorMessage(e) });
    }
  };
  const options: { value: IngestedCostSemantics; label: string; hint: string }[] = [
    {
      value: 'unconfirmed',
      label: 'Unconfirmed',
      hint: "Today's behaviour: counted in the Cost tile, absent from the P&L, and the P&L card says so.",
    },
    {
      value: 'real',
      label: 'Real ingested costs',
      hint: 'Money actually spent per row (traffic, compute, LLM): an Ingested cost line in every P&L layer and an Ingested column per feed.',
    },
    {
      value: 'model',
      label: 'The old modelled line',
      hint: "The Python app's estimate, not money: dropped from the Cost tile so it never double counts against Airman.",
    },
  ];
  return (
    <Card
      title="Ingested costs"
      status={<StatusPill tone={pill.tone}>{pill.text}</StatusPill>}
      description="The legacy import copied three per-row cost columns (traffic, compute, LLM) whose meaning was never recorded. Say what they are: real costs go on the P&L and per feed; the old model is dropped. Nothing is deleted either way — the choice only decides where the figures count."
    >
      <dl className="cfg-facts">
        <div>
          <dt>Rows carrying a cost</dt>
          <dd>{rows.toLocaleString('en-GB')}</dd>
        </div>
        <div>
          <dt>Σ of the three columns</dt>
          <dd>{fmtMoney(state.total)}</dd>
        </div>
        {state.nonUsdRows ? (
          <div>
            <dt>Non-USD rows</dt>
            <dd className="neg">
              {state.nonUsdRows.toLocaleString('en-GB')} — charged at face value, no FX (E8)
            </dd>
          </div>
        ) : null}
        <div>
          <dt>Meaning</dt>
          <dd>{state.semantics}</dd>
        </div>
      </dl>
      {note ? (
        <p className={note.tone === 'error' ? 'ui-field__error' : 'ui-field__hint'} role="status">
          {note.text}
        </p>
      ) : null}
      {isAdmin ? (
        <>
          <fieldset className="cfg-stack" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="ui-field__label">What are they?</legend>
            {options.map((o) => (
              <label key={o.value} className="ui-check">
                <input
                  type="radio"
                  name="ingested-cost-semantics"
                  value={o.value}
                  checked={choice === o.value}
                  onChange={() => setChoice(o.value)}
                />
                <span>
                  {o.label}
                  <span className="ui-check__hint">{o.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <Toolbar className="cfg-actions">
            <Button
              size="sm"
              loading={save.isPending}
              disabled={choice === state.semantics}
              onClick={() => void submit()}
            >
              Save decision
            </Button>
          </Toolbar>
        </>
      ) : (
        <EmptyState>{ADMIN_NOTE}</EmptyState>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------ invoices (P9) */

/** A YYYY-MM-DD the operator typed; anything else is not a day. */
function typedDay(v: string): string | null {
  return /^\d{4}-\d{2}-\d{2}$/.test(v.trim()) ? v.trim() : null;
}

/** An optional number field: blank = null (not given), otherwise a typed number >= 0 or invalid. */
function optionalNumber(v: string): { ok: true; value: number | null } | { ok: false } {
  if (v.trim() === '') return { ok: true, value: null };
  const n = typedNumber(v);
  return n == null ? { ok: false } : { ok: true, value: n };
}

/**
 * The monthly check from the RUNBOOK, in the product: the operator enters
 * each Undetect and Civo invoice, the server sums the mirror over its period
 * (launch_daily / hosting_daily ex-VAT), and the card shows the variance in
 * $ and %, the verdict (within 1% ok / over / under / unmirrored) and the
 * days of the period with no mirrored row. For Undetect the launch count is
 * checked separately from the bandwidth line, because a bill that agrees in
 * money can still hide unregistered launches. Amounts are ex-VAT; the total
 * is kept only for matching the paper.
 */
function InvoicesCard({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const invoices = useQuery({
    queryKey: ['invoices'],
    queryFn: async () =>
      listOf<InvoiceCheck>(await api.get<unknown>('/api/invoices'), 'checks', 'invoices'),
  });
  const [provider, setProvider] = useState<InvoiceProvider>('undetect');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [amountExVat, setAmountExVat] = useState('');
  const [amountTotal, setAmountTotal] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [source, setSource] = useState('');
  const [note, setNote] = useState('');
  const [launches, setLaunches] = useState('');
  const [launchUsd, setLaunchUsd] = useState('');
  const [bandwidthUsd, setBandwidthUsd] = useState('');
  const [msg, setMsg] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  // Only what the operator typed is ever sent: a blank amount disables Add
  // (Number('') is 0, and a $0 invoice is not an invoice); a blank optional
  // field is null, never 0.
  const start = typedDay(periodStart);
  const end = typedDay(periodEnd);
  const amount = typedRate(amountExVat);
  const total = optionalNumber(amountTotal);
  const launchesN = optionalNumber(launches);
  const launchUsdN = optionalNumber(launchUsd);
  const bandwidthUsdN = optionalNumber(bandwidthUsd);
  const periodOk = start != null && end != null && start <= end;
  const canAdd =
    periodOk &&
    amount != null &&
    total.ok &&
    launchesN.ok &&
    launchUsdN.ok &&
    bandwidthUsdN.ok &&
    /^[A-Z]{3}$/.test(currency.trim());

  const add = useMutation({
    // The route answers { ok, invoice }; older shapes returned the check bare.
    mutationFn: async (body: InvoiceInput) => {
      const res = await api.post<{ invoice?: InvoiceCheck } & Partial<InvoiceCheck>>(
        '/api/invoices',
        body,
      );
      return (res.invoice ?? (res as InvoiceCheck)) as InvoiceCheck;
    },
    onSuccess: (created) => {
      setMsg({
        tone: created?.verdict && created.verdict !== 'ok' ? 'error' : 'ok',
        text: created?.verdict
          ? `Added: ${VERDICT_LABEL[created.verdict]} — mirror ${fmtMoney(created.mirror?.total ?? 0)}, variance ${fmtMoney(created.variance ?? 0)}.`
          : 'Added.',
      });
      setAmountExVat('');
      setAmountTotal('');
      setLaunches('');
      setLaunchUsd('');
      setBandwidthUsd('');
      setNote('');
      void qc.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (e) => setMsg({ tone: 'error', text: errorMessage(e) }),
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/api/invoices/${id}`),
    onSuccess: () => {
      setMsg({ tone: 'ok', text: 'Invoice removed — the mirror is untouched.' });
      void qc.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (e) => setMsg({ tone: 'error', text: errorMessage(e) }),
  });
  const submit = () => {
    if (!canAdd || start == null || end == null || amount == null) return;
    if (!total.ok || !launchesN.ok || !launchUsdN.ok || !bandwidthUsdN.ok) return;
    add.mutate({
      provider,
      periodStart: start,
      periodEnd: end,
      amountExVat: amount,
      amountTotal: total.value,
      currency: currency.trim(),
      source: source.trim() || null,
      note: note.trim() || null,
      launches: provider === 'undetect' ? launchesN.value : null,
      launchUsd: provider === 'undetect' ? launchUsdN.value : null,
      bandwidthUsd: provider === 'undetect' ? bandwidthUsdN.value : null,
    });
  };
  const askRemove = async (inv: InvoiceCheck) => {
    const ok = await confirm<true>({
      title: `Remove the ${inv.provider} invoice for ${inv.periodStart} → ${inv.periodEnd}?`,
      body: <p>Only the check goes; the mirrored days stay.</p>,
      choices: [{ label: 'Remove', value: true, variant: 'danger' }],
    });
    if (ok) remove.mutate(inv.id);
  };

  const rows = invoices.data ?? [];
  const pct = (v: number | null) => (v == null ? '' : ` (${v.toFixed(1)}%)`);
  const cols: HideableColumn<InvoiceCheck>[] = [
    { key: 'Provider', get: (r) => r.provider, fixed: true },
    { key: 'Period', get: (r) => r.periodStart, fixed: true },
    { key: 'Invoice ex-VAT', get: (r) => r.amountExVat, fixed: true },
    { key: 'Total', get: (r) => r.amountTotal },
    { key: 'Mirror Σ', get: (r) => r.mirror?.total, fixed: true },
    { key: 'Variance', get: (r) => r.variance, fixed: true },
    { key: 'Verdict', get: (r) => r.verdict, fixed: true },
    { key: 'Launches', get: (r) => r.launchVariance ?? r.mirror?.launches },
    { key: 'Bandwidth', get: (r) => r.mirror?.bandwidthUsd },
    { key: 'Unmirrored days', get: (r) => r.mirror?.unmirroredDays?.length ?? 0 },
    { key: 'Note', get: (r) => r.note || r.source },
  ];
  const { shown, hidden } = visibleColumns(rows, cols);
  const cell = (r: InvoiceCheck, key: string): ReactNode => {
    switch (key) {
      case 'Provider':
        return r.provider;
      case 'Period':
        return `${r.periodStart} → ${r.periodEnd}`;
      case 'Invoice ex-VAT':
        return `${fmtMoney(r.amountExVat)}${r.currency && r.currency !== 'USD' ? ` ${r.currency}` : ''}`;
      case 'Total':
        return r.amountTotal != null ? fmtMoney(r.amountTotal) : '—';
      case 'Mirror Σ':
        return `${fmtMoney(r.mirror?.total ?? 0)} · ${r.mirror?.days ?? 0} day${r.mirror?.days === 1 ? '' : 's'}`;
      case 'Variance':
        return `${fmtMoney(r.variance)}${pct(r.variancePct)}`;
      case 'Verdict':
        return (
          <StatusPill tone={verdictTone(r.verdict)}>
            {VERDICT_LABEL[r.verdict] ?? r.verdict}
          </StatusPill>
        );
      case 'Launches':
        // The launch count check, apart from the money: invoice line vs the mirror's count.
        return r.provider === 'undetect' && r.mirror?.launches != null
          ? `mirror ${r.mirror.launches.toLocaleString('en-GB')}${r.launchVariance != null ? ` · invoice ${r.launchVariance >= 0 ? '+' : ''}${r.launchVariance.toLocaleString('en-GB')}` : ''}`
          : '—';
      case 'Bandwidth':
        return r.provider === 'undetect' && r.mirror?.bandwidthUsd != null
          ? `mirror ${fmtMoney(r.mirror.bandwidthUsd)}${r.mirror.launchUsd != null ? ` · launches ${fmtMoney(r.mirror.launchUsd)}` : ''}`
          : '—';
      case 'Unmirrored days': {
        const days = r.mirror?.unmirroredDays ?? [];
        return days.length ? <span className="inv-days">{days.join(', ')}</span> : '—';
      }
      case 'Note':
        return [r.source, r.note].filter(Boolean).join(' — ') || '—';
      default:
        return null;
    }
  };
  const cellClass = (r: InvoiceCheck, key: string): string | undefined => {
    if (key === 'Variance') return r.verdict === 'over' || r.verdict === 'under' ? 'neg' : 'dim';
    if (key === 'Launches')
      return r.launchVariance != null && r.launchVariance !== 0 ? 'neg' : 'dim';
    if (
      key === 'Provider' ||
      key === 'Period' ||
      key === 'Verdict' ||
      key === 'Note' ||
      key === 'Unmirrored days'
    )
      return 'l';
    return undefined;
  };

  return (
    <Card
      title="Invoices"
      status={
        rows.length ? (
          <StatusPill tone={rows.every((r) => r.verdict === 'ok') ? 'ok' : 'warn'}>
            {rows.filter((r) => r.verdict !== 'ok').length
              ? `${rows.filter((r) => r.verdict !== 'ok').length} to check`
              : `${rows.length} checked`}
          </StatusPill>
        ) : (
          <StatusPill tone="neutral">None entered</StatusPill>
        )
      }
      description="The monthly check: enter each Undetect and Civo invoice (ex-VAT) and see it against the mirror over its period — the variance in dollars and percent, a verdict (within 1% is ok), and any day of the period the mirror is missing. For Undetect the launch count is checked apart from the bandwidth line: a bill that agrees in money can still hide unregistered launches."
    >
      {invoices.isError ? (
        <p className="ui-field__error">
          Could not load the invoices: {errorMessage(invoices.error)}
        </p>
      ) : null}
      {rows.length ? (
        <div className="table-scroll">
          <table className="stat-table">
            <thead>
              <tr>
                {shown.map((c) => (
                  <th
                    key={c.key}
                    className={cellClass({} as InvoiceCheck, c.key) === 'l' ? 'l' : undefined}
                  >
                    {c.key}
                  </th>
                ))}
                {isAdmin ? <th className="l" /> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  {shown.map((c) => (
                    <td key={c.key} className={cellClass(r, c.key)}>
                      {cell(r, c.key)}
                    </td>
                  ))}
                  {isAdmin ? (
                    <td className="l">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={remove.isPending}
                        onClick={() => void askRemove(r)}
                      >
                        Delete
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : invoices.isSuccess ? (
        <EmptyState>
          No invoice entered yet. Add one below and it is checked against the mirror at once.
        </EmptyState>
      ) : null}
      {hidden.length ? <p className="table-note">{hiddenColumnsNote(hidden)}</p> : null}
      {msg ? (
        <p className={msg.tone === 'error' ? 'ui-field__error' : 'ui-field__hint'} role="status">
          {msg.text}
        </p>
      ) : null}
      {isAdmin ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="cfg-grid">
            <Field label="Provider">
              {(aria) => (
                <Select
                  {...aria}
                  value={provider}
                  onChange={(e) => setProvider(e.target.value as InvoiceProvider)}
                >
                  <option value="undetect">Undetect (launcher bill)</option>
                  <option value="civo">Civo (hosting)</option>
                </Select>
              )}
            </Field>
            <Field
              label="Currency"
              hint="USD only — both suppliers bill in USD and the mirrors are USD; another currency is refused."
            >
              {(aria) => (
                <TextInput
                  {...aria}
                  value={currency}
                  maxLength={3}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                />
              )}
            </Field>
            <Field label="Period start">
              {(aria) => (
                <TextInput
                  {...aria}
                  type="date"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                />
              )}
            </Field>
            <Field
              label="Period end (inclusive)"
              error={
                start != null && end != null && start > end
                  ? 'The period ends before it starts.'
                  : undefined
              }
            >
              {(aria) => (
                <TextInput
                  {...aria}
                  type="date"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                />
              )}
            </Field>
            <Field
              label="Amount ex-VAT"
              hint="What the mirror is checked against. A blank or 0 is never an invoice."
            >
              {(aria) => (
                <TextInput
                  {...aria}
                  type="number"
                  min={0}
                  step="0.01"
                  inputMode="decimal"
                  value={amountExVat}
                  onChange={(e) => setAmountExVat(e.target.value)}
                />
              )}
            </Field>
            <Field
              label="Invoice total (optional)"
              hint="Incl. VAT, for matching the paper — never charged."
            >
              {(aria) => (
                <TextInput
                  {...aria}
                  type="number"
                  min={0}
                  step="0.01"
                  inputMode="decimal"
                  value={amountTotal}
                  onChange={(e) => setAmountTotal(e.target.value)}
                />
              )}
            </Field>
            {provider === 'undetect' ? (
              <>
                <Field label="Launches on the invoice (optional)">
                  {(aria) => (
                    <TextInput
                      {...aria}
                      type="number"
                      min={0}
                      step="1"
                      inputMode="numeric"
                      value={launches}
                      onChange={(e) => setLaunches(e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Launch line $ (optional)">
                  {(aria) => (
                    <TextInput
                      {...aria}
                      type="number"
                      min={0}
                      step="0.01"
                      inputMode="decimal"
                      value={launchUsd}
                      onChange={(e) => setLaunchUsd(e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Bandwidth line $ (optional)">
                  {(aria) => (
                    <TextInput
                      {...aria}
                      type="number"
                      min={0}
                      step="0.01"
                      inputMode="decimal"
                      value={bandwidthUsd}
                      onChange={(e) => setBandwidthUsd(e.target.value)}
                    />
                  )}
                </Field>
              </>
            ) : null}
            <Field label="Invoice number or source (optional)">
              {(aria) => (
                <TextInput {...aria} value={source} onChange={(e) => setSource(e.target.value)} />
              )}
            </Field>
            <Field label="Note (optional)">
              {(aria) => (
                <TextInput {...aria} value={note} onChange={(e) => setNote(e.target.value)} />
              )}
            </Field>
          </div>
          <Toolbar className="cfg-actions">
            <Button type="submit" size="sm" loading={add.isPending} disabled={!canAdd}>
              Add invoice
            </Button>
          </Toolbar>
        </form>
      ) : (
        <EmptyState>{ADMIN_NOTE}</EmptyState>
      )}
      {dialog}
    </Card>
  );
}

/* --------------------------------------------------------- other costs (P10) */

/**
 * Fixed monthly costs billed outside Undetect and Civo (SaaS, domains,
 * bank fees - never an Airman fee, there is none, and never proxies, which
 * are Undetect's bandwidth line). Each is charged per day
 * pro-rata into the P&L's window - monthly × days of the window in that
 * month ÷ days in that month - as an Other costs line in Operating and
 * Overheads, never spread across feeds and never in the tiles. The charge
 * shown is the P&L's own figure for its default 30-day window, so this list
 * and the card agree by construction; the monthly amount is what was typed.
 */
function OverheadsCard({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const overheads = useQuery({
    queryKey: ['overheads'],
    queryFn: async () => listOf<OverheadRow>(await api.get<unknown>('/api/overheads'), 'overheads'),
  });
  // The same key the dashboard uses for its default range, so the cache is shared.
  const pl = useQuery({
    queryKey: ['pl', 30],
    queryFn: () => api.get<PLReport>('/api/pl?days=30'),
  });
  const charged = new Map((pl.data?.overheads?.items ?? []).map((i) => [i.id, i.charged]));
  const [editing, setEditing] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [monthly, setMonthly] = useState('');
  const [fromDay, setFromDay] = useState('');
  const [toDay, setToDay] = useState('');
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const monthlyN = typedRate(monthly);
  const from = typedDay(fromDay);
  const to = toDay.trim() === '' ? null : typedDay(toDay);
  const toOk = toDay.trim() === '' || (to != null && from != null && to >= from);
  const canSave = name.trim() !== '' && monthlyN != null && from != null && toOk;

  const reset = () => {
    setEditing(null);
    setName('');
    setMonthly('');
    setFromDay('');
    setToDay('');
    setNote('');
  };
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['overheads'] });
    void qc.invalidateQueries({ queryKey: ['pl'] });
  };
  const save = useMutation({
    mutationFn: (body: OverheadInput) => api.post<OverheadRow>('/api/overheads', body),
    onSuccess: () => {
      // An edit is retroactive: the P&L charges the new amount on every window
      // the entry's life overlaps, closed months included (E17). To change a
      // cost from a date, close the entry with To and add a new one.
      setMsg({
        tone: 'ok',
        text:
          editing == null
            ? 'Added — charged pro-rata from its first day.'
            : 'Updated — every window charges the new amount, past ones included. To change a cost from a date, close this entry with To and add a new one.',
      });
      reset();
      invalidate();
    },
    onError: (e) => setMsg({ tone: 'error', text: errorMessage(e) }),
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/api/overheads/${id}`),
    onSuccess: () => {
      setMsg({ tone: 'ok', text: 'Removed — the P&L no longer charges it.' });
      invalidate();
    },
    onError: (e) => setMsg({ tone: 'error', text: errorMessage(e) }),
  });
  const submit = () => {
    if (!canSave || monthlyN == null || from == null) return;
    save.mutate({
      ...(editing != null ? { id: editing } : {}),
      name: name.trim(),
      monthlyUsd: monthlyN,
      fromDay: from,
      toDay: to,
      note: note.trim() || null,
    });
  };
  const edit = (r: OverheadRow) => {
    setEditing(r.id);
    setName(r.name);
    setMonthly(String(r.monthlyUsd));
    setFromDay(r.fromDay);
    setToDay(r.toDay ?? '');
    setNote(r.note ?? '');
  };
  const askRemove = async (r: OverheadRow) => {
    const ok = await confirm<true>({
      title: `Remove "${r.name}"?`,
      body: <p>The P&L stops charging it for every window, past ones included.</p>,
      choices: [{ label: 'Remove', value: true, variant: 'danger' }],
    });
    if (ok) remove.mutate(r.id);
  };

  const rows = overheads.data ?? [];
  const cols: HideableColumn<OverheadRow>[] = [
    { key: 'Name', get: (r) => r.name, fixed: true },
    { key: 'Monthly USD', get: (r) => r.monthlyUsd, fixed: true },
    { key: 'From', get: (r) => r.fromDay, fixed: true },
    // An open-ended entry's end is a VALUE ("open"), so the all-empty rule
    // never hides the column when every entry runs open.
    { key: 'To', get: (r) => r.toDay ?? 'open' },
    { key: 'Charged, last 30 days', get: (r) => charged.get(r.id), fixed: true },
    { key: 'Note', get: (r) => r.note },
  ];
  const { shown, hidden } = visibleColumns(rows, cols);
  const cell = (r: OverheadRow, key: string): ReactNode => {
    switch (key) {
      case 'Name':
        return r.name;
      case 'Monthly USD':
        return fmtMoney(r.monthlyUsd);
      case 'From':
        return r.fromDay;
      case 'To':
        return r.toDay ?? 'open';
      case 'Charged, last 30 days': {
        const c = charged.get(r.id);
        return c != null ? fmtMoney(c) : pl.isSuccess ? '—' : '…';
      }
      case 'Note':
        return r.note ?? '—';
      default:
        return null;
    }
  };
  const left = (key: string) =>
    key === 'Name' || key === 'From' || key === 'To' || key === 'Note' ? 'l' : undefined;
  const windowTotal = pl.data?.overheads?.otherOverheads;

  return (
    <Card
      title="Other costs"
      status={
        rows.length ? (
          <StatusPill tone="ok">{`${rows.length} entr${rows.length === 1 ? 'y' : 'ies'}`}</StatusPill>
        ) : (
          <StatusPill tone="neutral">None</StatusPill>
        )
      }
      description="Fixed monthly costs billed outside Undetect and Civo — SaaS, domains, bank fees. Not the launcher or the proxies: those are the Undetect bill, and there is no Airman fee. Each is charged per day pro-rata (monthly × days of the window in that month ÷ days in that month) as an Other costs line in Operating and Overheads on the P&L, never spread across feeds and never in the tiles. Nothing is seeded: enter what the business actually pays."
    >
      {overheads.isError ? (
        <p className="ui-field__error">
          Could not load the entries: {errorMessage(overheads.error)}
        </p>
      ) : null}
      {rows.length ? (
        <>
          <div className="table-scroll">
            <table className="stat-table">
              <thead>
                <tr>
                  {shown.map((c) => (
                    <th key={c.key} className={left(c.key)}>
                      {c.key}
                    </th>
                  ))}
                  {isAdmin ? <th className="l" /> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    {shown.map((c) => (
                      <td key={c.key} className={left(c.key)}>
                        {cell(r, c.key)}
                      </td>
                    ))}
                    {isAdmin ? (
                      <td className="l">
                        <Toolbar>
                          <Button variant="ghost" size="sm" onClick={() => edit(r)}>
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={remove.isPending}
                            onClick={() => void askRemove(r)}
                          >
                            Delete
                          </Button>
                        </Toolbar>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="table-note">
            Charged, last 30 days is the P&L's own figure for its default window (
            {pl.data ? `${pl.data.since} → ${pl.data.upto}` : '…'}
            {windowTotal != null ? `; ${fmtMoney(windowTotal)} in all` : ''}); the dashboard's other
            ranges charge the same entries over their own days.
            {hidden.length ? ` ${hiddenColumnsNote(hidden)}` : ''}
          </p>
        </>
      ) : overheads.isSuccess ? (
        <EmptyState>
          No other costs entered. Anything the business pays monthly that is not the launcher or
          hosting bill belongs here.
        </EmptyState>
      ) : null}
      {msg ? (
        <p className={msg.tone === 'error' ? 'ui-field__error' : 'ui-field__hint'} role="status">
          {msg.text}
        </p>
      ) : null}
      {isAdmin ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="cfg-grid">
            <Field label="Name" hint="What it is — the P&L names it.">
              {(aria) => (
                <TextInput {...aria} value={name} onChange={(e) => setName(e.target.value)} />
              )}
            </Field>
            <Field label="Monthly USD" hint="Above 0 — a blank or 0 is never saved.">
              {(aria) => (
                <TextInput
                  {...aria}
                  type="number"
                  min={0}
                  step="0.01"
                  inputMode="decimal"
                  value={monthly}
                  onChange={(e) => setMonthly(e.target.value)}
                />
              )}
            </Field>
            <Field label="From">
              {(aria) => (
                <TextInput
                  {...aria}
                  type="date"
                  value={fromDay}
                  onChange={(e) => setFromDay(e.target.value)}
                />
              )}
            </Field>
            <Field
              label="To (blank = open-ended)"
              error={!toOk ? 'The end is before the start.' : undefined}
            >
              {(aria) => (
                <TextInput
                  {...aria}
                  type="date"
                  value={toDay}
                  onChange={(e) => setToDay(e.target.value)}
                />
              )}
            </Field>
            <Field label="Note (optional)">
              {(aria) => (
                <TextInput {...aria} value={note} onChange={(e) => setNote(e.target.value)} />
              )}
            </Field>
          </div>
          <Toolbar className="cfg-actions">
            <Button type="submit" size="sm" loading={save.isPending} disabled={!canSave}>
              {editing != null ? 'Save changes' : 'Add cost'}
            </Button>
            {editing != null ? (
              <Button variant="ghost" size="sm" onClick={reset}>
                Cancel edit
              </Button>
            ) : null}
          </Toolbar>
        </form>
      ) : (
        <EmptyState>{ADMIN_NOTE}</EmptyState>
      )}
      {dialog}
    </Card>
  );
}
